/**
 * Shared rules for a coach's own Wheel of Life page
 * (`/{username}/wheel-of-life`): the button that sends a visitor to the coach,
 * and the coach's own eight categories. Safe to import anywhere.
 *
 * Aliases: coach wheel, coach assessment page, coach wheel categories,
 * results contact button
 */

import { z } from "zod";

import {
  shouldShowBookingCTAs,
  shouldShowMessagingCTAs,
  type MinimalCoachData,
} from "~/lib/coach-cta-utils";
import {
  DEFAULT_WHEEL_LABELS,
  WHEEL_CATEGORY_COUNT,
  WHEEL_LABEL_MAX_LENGTH,
  WheelSessionInputSchema,
} from "~/lib/shared/wheel";

/**
 * A finished Wheel as the client sends it. On a coach page it names the page's
 * username, never a coach id: the server resolves the coach itself, and any
 * other field is stripped.
 */
export const CoachPageWheelSessionInputSchema = WheelSessionInputSchema.extend({
  coachUsername: z.string().min(1).max(100).optional(),
});

export function coachWheelPath(username: string): string {
  return `/${username}/wheel-of-life`;
}

/** Where a visitor goes to reach the coach, with the button's label. */
export interface CoachContactRoute {
  kind: "booking" | "messaging";
  href: string;
  label: string;
}

/**
 * Follows the coach's contact setting, as the profile's main button does:
 * "booking" opens the booking page when a calendar is stored and gives no
 * route otherwise; any other setting (including none saved) opens the message
 * form. A coach page cannot go live without a route.
 */
export function coachContactRoute(
  coach: MinimalCoachData & { firstName: string },
): CoachContactRoute | null {
  if (coach.profile.contactMode === "booking") {
    if (!shouldShowBookingCTAs(coach)) return null;
    return {
      kind: "booking",
      href: `/${coach.username}/book`,
      label: `Book a call with ${coach.firstName}`,
    };
  }
  if (!shouldShowMessagingCTAs(coach)) return null;
  return {
    kind: "messaging",
    href: `/${coach.username}/contact`,
    label: `Message ${coach.firstName}`,
  };
}

/** Why a coach cannot turn her Wheel page on. */
export type CoachWheelActivationRefusal =
  | "not_approved"
  | "no_profile"
  | "pre_generated"
  | "no_username"
  | "no_contact_route";

/** What the coach's Lead magnets page shows. */
export interface CoachWheelSettings {
  username: string;
  isActive: boolean;
  labels: string[];
  hasCustomLabels: boolean;
  isPublic: boolean;
  activationRefusal: CoachWheelActivationRefusal | null;
  contact: CoachContactRoute | null;
  leadMagnet: { title: string } | null;
}

/** The categories a coach's Wheel uses: their own, else the standard eight. */
export function coachWheelLabels(labels: readonly string[] | null): string[] {
  return [...(labels ?? DEFAULT_WHEEL_LABELS)];
}

export function sameWheelLabels(
  a: readonly string[],
  b: readonly string[],
): boolean {
  return a.length === b.length && a.every((label, i) => label === b[i]);
}

/**
 * A coach's own eight categories: trimmed, none empty, at most
 * WHEEL_LABEL_MAX_LENGTH characters, and all different ignoring case, because
 * results name the strongest and most stretched areas. Each issue sits on its
 * field's index so the editor can show it next to that field.
 */
export const CoachWheelLabelsSchema = z
  .array(z.string().max(200))
  .length(WHEEL_CATEGORY_COUNT)
  .transform((labels) => labels.map((label) => label.trim()))
  .superRefine((labels, ctx) => {
    const seen = new Map<string, number>();
    labels.forEach((label, index) => {
      if (label === "") {
        ctx.addIssue({
          code: "custom",
          path: [index],
          message: "Enter a name for this area.",
        });
        return;
      }
      if (label.length > WHEEL_LABEL_MAX_LENGTH) {
        ctx.addIssue({
          code: "custom",
          path: [index],
          message: `Use ${WHEEL_LABEL_MAX_LENGTH} characters or fewer.`,
        });
        return;
      }
      const key = label.toLocaleLowerCase();
      const first = seen.get(key);
      if (first !== undefined) {
        ctx.addIssue({
          code: "custom",
          path: [index],
          message: `"${label}" is already category ${first + 1}. Use a different name.`,
        });
        return;
      }
      seen.set(key, index);
    });
  });

/** One message per field, `null` where the field is fine. */
export function coachWheelLabelErrors(
  labels: readonly string[],
): (string | null)[] {
  const errors: (string | null)[] = Array(WHEEL_CATEGORY_COUNT).fill(null);
  const result = CoachWheelLabelsSchema.safeParse(labels);
  if (result.success) return errors;
  for (const issue of result.error.issues) {
    const index = issue.path[0];
    if (typeof index === "number" && errors[index] === null) {
      errors[index] = issue.message;
    }
  }
  return errors;
}
